import React, { useCallback, useEffect, useRef, useState } from 'react';
import { sendSlackNotification } from '../utils/slackNotifications';
import '../styles/components/ContactModal.css';

const EMPTY_FORM = {
  name: '',
  companyName: '',
  email: '',
  phone: '',
  subject: 'New project',
  message: '',
};

const COPY = {
  demo: {
    title: 'Book a call',
    blurb: 'Pick a topic and tell us a little about the project. We will come back with a time.',
  },
  consultation: {
    title: 'Talk to us',
    blurb: 'A short note is enough. We reply within one business day with next steps.',
  },
  information: {
    title: 'Request information',
    blurb: 'Tell us what you would like to know and we will send the details across.',
  },
};

function ConsultationModal({ isOpen, onClose, modalType, prefill }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !prefill) return;

    setFormData((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(prefill)) {
        if (prefill[key] === undefined || prefill[key] === null) continue;
        if (typeof prev[key] === 'string' && prev[key].trim().length > 0) continue;
        next[key] = prefill[key];
      }
      // Always prefill message when provided (it's the most useful context field)
      if (typeof prefill.message === 'string' && prefill.message.trim().length > 0) {
        next.message = prefill.message;
      }
      return next;
    });
  }, [isOpen, prefill]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscKey = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscKey);
    document.body.style.overflow = 'hidden';
    firstFieldRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await sendSlackNotification({ ...formData, requestType: modalType }, 'consultation');
    setIsSubmitted(true);

    setTimeout(() => {
      setFormData(EMPTY_FORM);
      setIsSubmitted(false);
      onClose();
    }, 5000);
  };

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!isOpen) return null;

  const { title, blurb } = COPY[modalType] || COPY.demo;

  return (
    <div className="cm-overlay" onClick={handleOverlayClick}>
      <div className="cm-dialog" role="dialog" aria-modal="true" aria-labelledby="cm-title">
        <div className="cm-head">
          <span className="cm-head-glow" aria-hidden="true" />
          <span className="cm-kicker">Contact</span>
          <h2 id="cm-title">{title}</h2>
          <p>{blurb}</p>
          <button type="button" className="cm-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6 6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="cm-body">
          {isSubmitted ? (
            <div className="cm-success">
              <div className="cm-success-mark" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="m5 13 4 4L19 7"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>Message received</h3>
              <p>Thanks. We have your note and will reply within one business day, usually sooner.</p>
              <p>
                If it is urgent, call us at <strong>+91 80073 85851</strong> during working hours.
              </p>
            </div>
          ) : (
            <form className="cm-form" onSubmit={handleSubmit}>
              <div className="cm-row">
                <div className="cm-field">
                  <label htmlFor="cm-name">Your name</label>
                  <input
                    ref={firstFieldRef}
                    type="text"
                    id="cm-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Jane Doe"
                    required
                  />
                </div>
                <div className="cm-field">
                  <label htmlFor="cm-company">Company</label>
                  <input
                    type="text"
                    id="cm-company"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Your company"
                  />
                </div>
              </div>

              <div className="cm-row">
                <div className="cm-field">
                  <label htmlFor="cm-email">Email</label>
                  <input
                    type="email"
                    id="cm-email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    required
                  />
                </div>
                <div className="cm-field">
                  <label htmlFor="cm-phone">Phone</label>
                  <input
                    type="tel"
                    id="cm-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>

              <div className="cm-field">
                <label htmlFor="cm-subject">What is this about?</label>
                <select
                  id="cm-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                >
                  <option value="New project">New project</option>
                  <option value="Ongoing work">Ongoing work / retainer</option>
                  <option value="Pricing">Pricing question</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Support">Existing project support</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="cm-field">
                <label htmlFor="cm-message">Your message</label>
                <textarea
                  id="cm-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="What are you building, who is it for, and when do you want to start?"
                  rows="5"
                  required
                ></textarea>
              </div>

              <div className="cm-actions">
                <button type="button" className="cm-cancel" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="cm-submit">
                  Send message
                </button>
              </div>

              <p className="cm-foot">We reply within one business day. No spam, no automated drip.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConsultationModal;
